// TableComponent.jsx
import React, { Fragment } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

/**
 * Props:
 * - columns: [{ label, field, align, renderCell? }]
 * - rows: any[]
 * - onRowClick?: (row) => void
 * - renderRowBottom?: (row) => ReactNode
 * - getRowId?: (row) => string|number   // <-- NEW (preferred)
 * - rowKeyField?: string                // <-- fallback if you don't pass getRowId
 * - getColId?: (col, index) => string   // <-- NEW (optional; helps header/cell keys)
 */
const TableComponent = ({
    columns,
    rows,
    onRowClick,
    renderRowBottom,
    getRowId,
    rowKeyField = 'id',
    getColId,
}) => {
    const stopPropagation = (e) => e.stopPropagation();

    const resolveRowId = (row, idx) => {
        if (typeof getRowId === 'function') return getRowId(row);
        // fallback: try field, else last resort index (not ideal)
        return row?.[rowKeyField] ?? row?.id ?? row?.key ?? idx;
    };

    const resolveColId = (col, idx) => {
        if (typeof getColId === 'function') return getColId(col, idx);
        return col.field || col.label || String(idx);
    };

    return (
        <Box>
            <Table>
                <TableHead>
                    <TableRow>
                        {columns.map((c, i) => (
                            <TableCell
                                key={resolveColId(c, i)}
                                align={c.align || 'left'}
                            >
                                <Typography
                                    sx={{
                                        color: '#292929',
                                        fontSize: '14px',
                                        lineHeight: '21px',
                                    }}
                                >
                                    {c.label}
                                </Typography>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows.map((row, idx) => {
                        const rowId = resolveRowId(row, idx);

                        return (
                            <Fragment key={rowId}>
                                <TableRow
                                    hover
                                    sx={{
                                        cursor: onRowClick
                                            ? 'pointer'
                                            : 'default',
                                    }}
                                    onClick={() =>
                                        onRowClick && onRowClick(row)
                                    }
                                >
                                    {columns.map((c, j) => {
                                        const value = row[c.field];
                                        const colKey = resolveColId(c, j); // stable per column
                                        return (
                                            <TableCell
                                                key={`${rowId}-${colKey}`}
                                                align={c.align || 'left'}
                                                sx={{
                                                    color: '#292929',
                                                    borderBottom:
                                                        renderRowBottom
                                                            ? 'none'
                                                            : undefined,
                                                }}
                                            >
                                                {c.renderCell
                                                    ? c.renderCell(
                                                          value,
                                                          row,
                                                          stopPropagation
                                                      )
                                                    : value}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>

                                {renderRowBottom && (
                                    <TableRow key={`${rowId}-bottom`}>
                                        <TableCell
                                            colSpan={columns.length}
                                            sx={{
                                                py: 0.5,
                                                backgroundColor: '#f9f9f9',
                                            }}
                                        >
                                            <Box
                                                onClick={stopPropagation}
                                                display="flex"
                                                justifyContent="flex-start"
                                                gap={2}
                                            >
                                                {renderRowBottom(row)}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </Fragment>
                        );
                    })}
                </TableBody>
            </Table>
        </Box>
    );
};

export default TableComponent;
